import React, { useState } from "react";
import { Box, Chip, Grid, Paper, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import WarningIcon from "@mui/icons-material/Warning";

const mockJobs = [
  { job_id: "J101", status: "COMPLETED", start_time: new Date().toISOString() },
  { job_id: "J102", status: "COMPLETED", start_time: new Date().toISOString() },
  { job_id: "J103", status: "PAUSED", start_time: new Date().toISOString() },
  { job_id: "J104", status: "FAILED", start_time: new Date().toISOString() },
];

const mockAssetSimData = {
  schematic: "https://connect-api-prod.getvia.ai/static/defect/0703.jpg",
  params: [
    { label: "Voltage", value: "220V" },
    { label: "Current", value: "10A" },
  ],
  risk: [
    { label: "Overheat Risk", level: "High" },
    { label: "Power Surge", level: "Medium" },
  ],
  safety: {
    hazard: "High temperature detected in the main core.",
    control: "Activate cooling system and reduce load.",
    probability: "High",
  },
};

export const ToolDetailsMockComponent = () => {
  const [selectedTool, setSelectedTool] = useState("DrillBot-2000");
  const jobs = mockJobs;
  const assetSimData = mockAssetSimData;
  const totalTimeLabel = "2h 30m";

  const handleTimelineBlockClick = (job: any) => {
    alert(`Clicked on job ${job.job_id}`);
  };

  return (
    <Grid container spacing={3} alignItems="stretch">
      <Grid
        item
        xs={12}
        md="auto"
        sx={{
          flexGrow: 0,
          minWidth: 320,
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Paper
          sx={{
            p: 1,
            mb: 1,
            bgcolor: "background.paper",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            flex: 1,
          }}
        >
          {selectedTool && (
            <Box
              sx={{
                width: "100%",
                bgcolor:
                  jobs.filter((j) => j.status === "COMPLETED").length >= 3
                    ? "#43a047"
                    : "#e53935",
                color: "#fff",
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                px: 2,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                minHeight: 48,
                mb: 1,
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#fff", lineHeight: 1 }}
                >
                  {selectedTool}
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontWeight: 400,
                    fontSize: 15,
                    mt: 0.5,
                  }}
                >
                  {jobs[jobs.length - 1]?.job_id}
                </Typography>
              </Box>
              <Box
                sx={{
                  bgcolor: "#fff",
                  color: "#43a047",
                  borderRadius: 8,
                  px: 2,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 700,
                  fontSize: 15,
                }}
              >
                <AccessTimeIcon sx={{ fontSize: 18, mr: 0.5 }} />
                {totalTimeLabel}
              </Box>
            </Box>
          )}

          {assetSimData && (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "hidden",
                p: 0,
              }}
            >
              <img
                src={assetSimData.schematic}
                alt={`${selectedTool} Schematic`}
                style={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "200px",
                  objectFit: "contain",
                }}
              />
            </Box>
          )}

          {selectedTool && (
            <Box sx={{ width: "100%", mt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 16,
                  borderRadius: 2,
                  overflow: "hidden",
                  background: "#e8f5e9",
                  border: "1px solid #43a047",
                }}
              >
                {(jobs || []).map((job, idx) => {
                  const startTime = new Date(job.start_time);
                  const hour = startTime.getHours();
                  return (
                    <Box
                      key={job.job_id}
                      sx={{
                        flex: 1,
                        height: "100%",
                        bgcolor:
                          job.status === "COMPLETED"
                            ? "#43a047"
                            : job.status === "PAUSED"
                            ? "#ff9800"
                            : "#e53935",
                        transition: "background 0.3s",
                        borderRight:
                          idx < jobs.length - 1 ? "1px solid #fff" : "none",
                        cursor: "pointer",
                        "&:hover": { opacity: 0.7 },
                      }}
                      title={`Job ${job.job_id}: ${job.status} @ ${hour}:00`}
                      onClick={() => {}}
                    />
                  );
                })}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: 0.25,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: 10 }}
                >
                  00:00
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: 10 }}
                >
                  12:00
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: 10 }}
                >
                  24:00
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>

      <Grid
        item
        xs={12}
        md
        sx={{
          minWidth: 280,
          maxWidth: 400,
          flexGrow: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
          {assetSimData && (
            <Paper
              sx={{ p: 1, flex: 1, bgcolor: "background.paper", width: "100%" }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Operational Parameters
              </Typography>
              {(assetSimData.params || []).map((param) => (
                <Typography
                  key={param.label}
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  {param.label}:{" "}
                  <span style={{ fontWeight: 400 }}>{param.value}</span>
                </Typography>
              ))}
            </Paper>
          )}

          {assetSimData && (
            <Paper
              sx={{ p: 1, flex: 1, bgcolor: "background.paper", width: "100%" }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Risk
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {(assetSimData.risk || []).map((r) => (
                  <Box
                    key={r.label}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor:
                          r.level === "Low"
                            ? "success.main"
                            : r.level === "Medium"
                            ? "warning.main"
                            : "error.main",
                      }}
                    />
                    <Typography variant="body2">
                      {r.label} - {r.level}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>

        {assetSimData && (
          <Paper
            sx={{
              p: 1,
              mb: 1,
              bgcolor: "background.paper",
              width: "100%",
              flex: 1,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Operational Safety Intelligence
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Hazard Description
            </Typography>
            <Typography variant="body2">
              {assetSimData.safety.hazard}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, mt: 1 }}>
              Control Measure
            </Typography>
            <Typography variant="body2">
              {assetSimData.safety.control}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Probability
              </Typography>
              <Chip
                size="small"
                label={assetSimData.safety.probability}
                color="error"
                sx={{ ml: 1 }}
              />
            </Box>
            <WarningIcon sx={{ fontSize: 40, color: "warning.main", mt: 1 }} />
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};
